/**
 * QuickLookZoom module
 * Handles the logic required for zooming product imagery within the quicklook modal overlay.
 */
export default class QuickLookZoom {
  zoomTriggerOrigin;

  constructor() {
    this.zoomTriggerOrigin = null;
    this.initListeners();
  }

  /**
   * Initializes event listeners required for the zoom functionality using vanilla JS event delegation.
   */
  initListeners() {
    // Combine all click listeners into a single delegated document listener
    document.addEventListener('click', (e) => {
      // 1. Open zoom overlay on clicking a zoomable thumbnail image
      const zoomImg = e.target.closest('[data-product-scrollable-images] .product-main img[data-zoom]');
      if (zoomImg) {
        this.openZoomOverlay(zoomImg);
        return;
      }

      // 2. Close zoom overlay on close button click
      const closeBtn = e.target.closest('[data-zoom-close]');
      if (closeBtn) {
        const overlay = closeBtn.closest('.product-zoom-overlay');
        this.closeZoomOverlay(overlay);
        return;
      }

      // 3. Close zoom overlay on clicking the backdrop, scrollwrap, or the image itself
      const overlay = e.target.closest('.product-zoom-overlay');
      if (overlay) {
        if (e.target === overlay || e.target.classList.contains('product-zoom-overlay__scrollwrap') || e.target.tagName.toLowerCase() === 'img') {
          this.closeZoomOverlay(overlay);
        }
      }
    });
    
    // Accessibility: Open zoom overlay via Enter or Space key
    document.addEventListener('keypress', (e) => {
      const zoomImg = e.target.closest('[data-product-scrollable-images] .product-main img[data-zoom]');
      if (zoomImg && (e.which === 13 || e.which === 32)) {
        e.preventDefault();
        this.openZoomOverlay(zoomImg);
      }
    });

    // Accessibility: Close zoom overlay via Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        const activeOverlay = document.querySelector('.product-zoom-overlay.active');
        if (activeOverlay) {
          this.closeZoomOverlay(activeOverlay);
        }
      }
    });
  }

  /**
   * Closes the active zoom overlay gracefully, clears its image content
   * to avoid stale state, and restores focus back to the triggering element.
   * 
   * @param {HTMLElement} overlay - The zoom overlay element to close
   */
  closeZoomOverlay = (overlay) => {
    if (!overlay) return;
    overlay.classList.remove('active');
    overlay.setAttribute('aria-hidden', 'true');
    // Clear stale image src after the fade-out transition
    overlay.addEventListener('transitionend', () => {
      const img = overlay.querySelector('img');
      if (img) img.src = '';
    }, { once: true });
    
    const mediaContainer = document.querySelector('#quicklook-modal [data-product-scrollable-images]');
    if (mediaContainer) {
      mediaContainer.style.overflow = '';
      const imageGrid = mediaContainer.querySelector('[data-product-full]');
      if (imageGrid) imageGrid.style.opacity = '1';
    }

    if (this.zoomTriggerOrigin) {
      this.zoomTriggerOrigin.setAttribute('aria-expanded', 'false');
      this.zoomTriggerOrigin.focus();
      this.zoomTriggerOrigin = null;
    }
  };

  /**
   * Caches or injects the DOM structure for the zoom overlay dynamically 
   * into the given media container if it doesn't already exist.
   * 
   * @param {HTMLElement} mediaContainer - The scrollable images container
   * @returns {HTMLElement} The zoom overlay DOM element
   */
  getOrCreateZoomOverlay = (mediaContainer) => {
    let overlay = mediaContainer.querySelector('.product-zoom-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'product-zoom-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-label', 'Zoomed product image');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('tabindex', '-1');
      overlay.innerHTML = `
        <button class="product-zoom-overlay__close" aria-label="Close zoomed image" data-zoom-close></button>
        <div class="product-zoom-overlay__scrollwrap">
          <img src="" alt="Zoomed product image" />
        </div>
      `;
      mediaContainer.appendChild(overlay);
    }
    return overlay;
  };

  /**
   * Activates the zoom overlay by injecting the large format image, 
   * trapping focus on the close button, hiding the original image grid,
   * and capturing the origin triggering element.
   * 
   * @param {HTMLElement} img - The image element that triggered the zoom
   */
  openZoomOverlay = (img) => {
    const zoomSrc = img.dataset.zoom;
    if (!zoomSrc) return;
    
    const mediaContainer = document.querySelector('#quicklook-modal [data-product-scrollable-images]');
    if (!mediaContainer) return;

    this.zoomTriggerOrigin = img;
    img.setAttribute('aria-expanded', 'true');

    const overlay = this.getOrCreateZoomOverlay(mediaContainer);
    const zoomImg = overlay.querySelector('img');
    zoomImg.src = zoomSrc;
    zoomImg.alt = img.alt;
    
    overlay.classList.add('active');
    overlay.setAttribute('aria-hidden', 'false');
    mediaContainer.scrollTop = 0;
    mediaContainer.style.overflow = 'hidden';
    
    const imageGrid = mediaContainer.querySelector('[data-product-full]');
    if (imageGrid) imageGrid.style.opacity = '0';
    
    // Shift focus to close button for ADA
    const closeBtn = overlay.querySelector('[data-zoom-close]');
    if (closeBtn) closeBtn.focus();
  };
}
